import { defineStore } from 'pinia'
import {
  ADMIN_ORDERS_PAGE_SIZE,
  executeAdminOrderActionApi,
  fetchAdminOrderByIdApi,
  fetchAdminOrdersApi,
} from '@/entities/admin/api/adminOrdersApi'
import { mapAdminOrderDetailToListItem } from '@/entities/order/model/mapAdminOrder'

export const useAdminOrdersStore = defineStore('adminOrders', {
  state: () => ({
    selectedOrderId: null,
    activeOrder: null,
    isActiveOrderLoading: false,
    isActiveOrderUpdating: false,
    activeOrderError: null,
    orderList: [],
    orderListTotal: 0,
    isOrderListLoading: false,
    isLoadingMore: false,
    orderListError: null,
    hasLoadedOrderList: false,
    pageIndex: 0,
    pageSize: ADMIN_ORDERS_PAGE_SIZE,
    sortField: 'created_at',
    sortOrder: -1,
    filters: {
      orderId: '',
      clientId: '',
      restaurantId: '',
      status: [],
    },
  }),
  getters: {
    hasMoreOrders: (state) => state.orderList.length < state.orderListTotal,
  },
  actions: {
    clear() {
      this.selectedOrderId = null
      this.activeOrder = null
      this.isActiveOrderLoading = false
      this.isActiveOrderUpdating = false
      this.activeOrderError = null
      this.orderList = []
      this.orderListTotal = 0
      this.isOrderListLoading = false
      this.isLoadingMore = false
      this.orderListError = null
      this.hasLoadedOrderList = false
      this.pageIndex = 0
      this.pageSize = ADMIN_ORDERS_PAGE_SIZE
      this.sortField = 'created_at'
      this.sortOrder = -1
      this.filters = {
        orderId: '',
        clientId: '',
        restaurantId: '',
        status: [],
      }
    },
    setSelectedOrderId(orderId) {
      this.selectedOrderId = orderId ? String(orderId) : null
    },
    mergeOrderInList(order) {
      if (!order?.id) {
        return
      }
      const index = this.orderList.findIndex((item) => item.id === order.id)
      if (index < 0) {
        return
      }
      const patch = mapAdminOrderDetailToListItem(order)
      if (!patch) {
        return
      }
      this.orderList[index] = { ...this.orderList[index], ...patch }
    },
    setFilters({ orderId = '', clientId = '', restaurantId = '', status = [] } = {}) {
      this.filters = {
        orderId: String(orderId ?? '').trim(),
        clientId: String(clientId ?? '').trim(),
        restaurantId: String(restaurantId ?? '').trim(),
        status: Array.isArray(status) ? status.filter(Boolean) : [],
      }
      this.pageIndex = 0
    },
    setPagination({ pageIndex = 0, pageSize = ADMIN_ORDERS_PAGE_SIZE } = {}) {
      this.pageIndex = Math.max(0, Number(pageIndex) || 0)
      this.pageSize = Math.max(1, Number(pageSize) || ADMIN_ORDERS_PAGE_SIZE)
    },
    setSorting({ sortField = 'created_at', sortOrder = -1 } = {}) {
      this.sortField = sortField || 'created_at'
      this.sortOrder = Number(sortOrder) === 1 ? 1 : -1
      this.pageIndex = 0
    },
    async loadOrdersPage() {
      const filter = {}
      if (this.filters.orderId) {
        filter.id = this.filters.orderId
      }
      if (this.filters.clientId) {
        filter.client_id = this.filters.clientId
      }
      if (this.filters.restaurantId) {
        filter.resto_id = this.filters.restaurantId
      }
      if (this.filters.status.length) {
        filter.status = { $in: this.filters.status }
      }

      const { items, total } = await fetchAdminOrdersApi({
        limit: this.pageSize,
        skip: this.pageIndex * this.pageSize,
        filter,
        sort: { [this.sortField]: this.sortOrder },
      })

      this.orderList = items
      this.orderListTotal = total
      this.hasLoadedOrderList = true
    },
    async fetchOrderList({ force = false } = {}) {
      if (this.isOrderListLoading || (this.hasLoadedOrderList && !force)) {
        return
      }

      this.isOrderListLoading = true
      this.orderListError = null
      try {
        await this.loadOrdersPage()
      } catch (error) {
        this.orderListError = error
      } finally {
        this.isOrderListLoading = false
      }
    },
    async loadSelectedOrder(orderId, { silent = false } = {}) {
      const id = orderId ?? this.selectedOrderId
      if (!id) {
        this.activeOrder = null
        return
      }

      if (String(id) !== String(this.selectedOrderId)) {
        this.setSelectedOrderId(id)
      }

      if (!silent) {
        this.isActiveOrderLoading = true
      }
      this.activeOrderError = null
      try {
        const order = await fetchAdminOrderByIdApi(id)
        this.activeOrder = order
        if (order) {
          this.mergeOrderInList(order)
        }
      } catch (error) {
        this.activeOrderError = error
        if (!silent) {
          this.activeOrder = null
        }
      } finally {
        if (!silent) {
          this.isActiveOrderLoading = false
        }
      }
    },
    async executeActiveOrderAction(action, body = {}) {
      if (!this.activeOrder?.id || !action?.path || this.isActiveOrderUpdating) {
        return
      }

      this.isActiveOrderUpdating = true
      this.activeOrderError = null

      try {
        const updated = await executeAdminOrderActionApi(this.activeOrder.id, action, body)
        if (updated) {
          this.activeOrder = updated
          this.mergeOrderInList(updated)
        }
        await this.fetchOrderList({ force: true })
      } catch (error) {
        await this.loadSelectedOrder(this.activeOrder.id)
        throw error
      } finally {
        this.isActiveOrderUpdating = false
      }
    },
  },
})
